import type { Client } from './generated/client';

/**
 * Adds Bearer token to every API request. Must be called once at app
 * startup with a getToken function from Clerk's useAuth().
 *
 * Because the interceptor is set up outside of React (in the module
 * scope of client.ts), we use a mutable reference that gets set once
 * the ClerkProvider has loaded.
 *
 * E2E note: when `VITE_E2E_AUTH_BYPASS=1` is set, the interceptor is a
 * no-op. Without a real Clerk session, `getToken()` from `useAuth`
 * doesn't reliably resolve, which would hang every outbound request.
 * The bypass build is dev-only and the env var is inlined at build time,
 * so production bundles always run the real interceptor.
 */
const E2E_BYPASS = import.meta.env.VITE_E2E_AUTH_BYPASS === '1';

let tokenGetter: (() => Promise<string | null>) | null = null;

export function setTokenGetter(fn: () => Promise<string | null>) {
  if (E2E_BYPASS) return;
  tokenGetter = fn;
}

export function registerAuthInterceptor(client: Client) {
  if (E2E_BYPASS) return;
  client.interceptors.request.use(async (request) => {
    if (tokenGetter) {
      const token = await tokenGetter();
      if (token) {
        // Request is immutable — clone with updated headers
        return new Request(request, {
          headers: new Headers([
            // Spread existing headers
            ...Array.from((request as Request).headers.entries()),
            ['Authorization', `Bearer ${token}`],
          ]),
        });
      }
    }
    return request;
  });
}
